import React, { useState } from 'react';
import { Loader } from '@aws-amplify/ui-react';
import { signIn, signInWithRedirect } from 'aws-amplify/auth';
import { useWhatsAppMode, setGlobalWhatsAppMode } from '../../../hooks/useWhatsAppMode';
import { useDispatch } from 'react-redux';
import { updateUserInRedux } from '../../../utils/updateUserInRedux';
import DividerWithText from '../shared/DividerWithText';
import GoogleSignInButton from '../shared/GoogleSignInButton';
import ForgotPassword from '../../shared/ForgotPassword';
import PasswordInput from '../../ui/PasswordInput';

export const WhatsAppSignIn = () => {
  const [mode] = useWhatsAppMode();
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotForm, setShowForgotForm] = useState(false);

  if (mode !== 'signIn') return null;

  const handleSignIn = async () => {
    if (!email || !password) {
      setError('Please enter email and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await signIn({ username: email, password });
      await updateUserInRedux(dispatch);
      setEmail('');
      setPassword('');
    } catch (err) {
      console.error('SignIn error:', err);
      
      if (err.code === 'UserNotConfirmedException') {
        setError('Please confirm your email first');
      } else if (err.code === 'NotAuthorizedException') {
        setError('Invalid email or password');
      } else if (err.code === 'UserNotFoundException') {
        setError('User account not found');
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Failed to sign in. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    // show the separate forgot-password component
    setShowForgotForm(true);
    setError(''); // clear sign-in error
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading) {
      handleSignIn();
    }
  };
  const handleGoogleSignIn = async () => {
    try {
      await signInWithRedirect({ provider: 'Google' });
    } catch (err) {
      console.error('Google sign in error:', err);
      if (err.message?.includes('scopes')) {
        setError('Please authorize all requested permissions for signin');
      } else {
        setError('Failed to sign in with Google');
      }
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    paddingLeft: '38px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '14px',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    background: '#f9fafb',
    color: '#1f2937',
  };

  const labelStyle = {
    display: 'block',
    textAlign: 'left',
    fontSize: '14px',
    fontWeight: '500',
    marginBottom: '8px',
    color: '#374151'
  };

  return (
    <div style={{ width: '100%' }}>
      {/* sign-in error only when not in forgot/reset view */}
      {!showForgotForm && error && (
        <div style={{
          marginBottom: '16px',
          padding: '12px',
          borderRadius: '6px',
          background: '#fee2e2',
          color: '#dc2626',
          fontSize: '14px',
          border: '1px solid #fca5a5'
        }}>
          {error}
        </div>
      )}

      {!showForgotForm && (
        <>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Email</label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: '12px', top: '10px', color: '#9ca3af' }}>✉️</span>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                onKeyPress={handleKeyPress}
                placeholder="email@company.com"
                style={inputStyle}
                disabled={loading}
              />
            </div>
          </div>

          <div style={{ marginBottom: '8px' }}>
            <PasswordInput
              label="Password"
              id="signin-password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              onKeyPress={handleKeyPress}
              placeholder="Enter your password"
              disabled={loading}
              labelStyle={{ textAlign: 'left' }}
            />
          </div>

          <div style={{
            marginBottom: '24px',
            textAlign: 'right'
          }}>
            <button
              type="button"
              onClick={handleForgotPassword}
              style={{
                background: 'none',
                border: 'none',
                color: '#2563eb',
                cursor: 'pointer',
                fontSize: '14px',
                textDecoration: 'none'
              }}
            >
              Forgot password?
            </button>
          </div>

          <button
            onClick={handleSignIn}
            disabled={loading}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: '6px',
              border: 'none',
              background: loading ? '#9ca3af' : '#2563eb',
              color: 'white',
              fontSize: '15px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {loading ? (
              <>
                <Loader size="small" /> Signing in...
              </>
            ) : (
              'Sign in'
            )}
          </button>
        </>
      )}

      {showForgotForm && (
        <ForgotPassword onClose={() => setShowForgotForm(false)} />
      )}

      <DividerWithText />

      <GoogleSignInButton text="Continue with Google" onClick={handleGoogleSignIn} />

      <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '14px', color: '#4b5563' }}>
        Don't have an account?{' '}
        <button
          type="button"
          onClick={() => setGlobalWhatsAppMode('signUp')}
          style={{
            background: 'none',
            border: 'none',
            color: '#26c485',
            cursor: 'pointer',
            fontWeight: '600',
            textDecoration: 'none',
            padding: '0',
            fontFamily: 'inherit',
            fontSize: 'inherit'
          }}
        >
          Create one here
        </button>
      </div>
    </div>
  );
};

export default WhatsAppSignIn;
